import { industryPacks } from "@/config/horizonscanning/packs";
import { suggestedPublisherIds } from "@/config/horizonscanning/sources";
import { topicLanguageLabel, topicLanguages, defaultTopicLanguage } from "@/config/horizonscanning/languages";
import {
  seedCustomTopic,
  type IndustryPack,
  type SearchTerm,
  type WizardState,
} from "@/config/horizonscanning/types";
import { cn } from "@/utils/cn";

interface TopicStepProps {
  pack: IndustryPack | undefined;
  state: WizardState;
  onChange: (patch: Partial<WizardState>) => void;
  /** Wizard defaults sources from the pack. Watchlist edit should leave sources alone. */
  resetSourcesOnPackChange?: boolean;
}

export function TopicStep({ pack, state, onChange, resetSourcesOnPackChange = true }: TopicStepProps) {
  const customOpen = Boolean(state.customTopic);
  const toggleCustom = (open: boolean) => {
    onChange({
      customTopic: open
        ? (state.customTopic ?? seedCustomTopic(pack?.topics[0]?.terms, pack?.topics[0]?.language ?? defaultTopicLanguage))
        : null,
    });
  };

  const selectPack = (packId: string) => {
    const next = industryPacks.find((p) => p.packId === packId);
    onChange({
      packId,
      packVersion: next?.packVersion ?? "",
      topicIds: [],
      ...(resetSourcesOnPackChange
        ? { publisherIds: suggestedPublisherIds(next?.suggestedSourceIds.slice(0, 1) ?? []) }
        : {}),
    });
  };

  const toggleTopic = (topicId: string) => {
    const selected = state.topicIds.includes(topicId);
    onChange({
      topicIds: selected ? state.topicIds.filter((id) => id !== topicId) : [...state.topicIds, topicId],
      includeAll: false,
    });
  };

  const patchCustom = (patch: Partial<NonNullable<WizardState["customTopic"]>>) => {
    if (!state.customTopic) return;
    onChange({ customTopic: { ...state.customTopic, ...patch } });
  };

  const updateKeyword = (index: number, next: SearchTerm) => {
    if (!state.customTopic) return;
    const terms = state.customTopic.terms.map((row, i) => (i === index ? next : row));
    patchCustom({ terms });
  };

  const addKeyword = () => {
    if (!state.customTopic) return;
    patchCustom({ terms: [...state.customTopic.terms, { term: "" }] });
  };

  const removeKeyword = (index: number) => {
    if (!state.customTopic) return;
    const terms =
      state.customTopic.terms.length <= 1
        ? [{ term: "" }]
        : state.customTopic.terms.filter((_, i) => i !== index);
    patchCustom({ terms });
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="mb-2 text-sm font-medium text-ink-soft">Industry pack</p>
        <div className="flex flex-wrap gap-2">
          {industryPacks.map((p) => (
            <button
              key={p.packId}
              type="button"
              className={cn(
                "rounded-full border px-3 py-1.5 text-sm font-medium",
                state.packId === p.packId
                  ? "border-brand-500 bg-brand-50 text-brand-800"
                  : "border-slate-300 bg-white text-ink-soft hover:border-brand-400",
              )}
              onClick={() => selectPack(p.packId)}
            >
              {p.industry}
            </button>
          ))}
        </div>
        {pack ? <p className="mt-2 text-sm text-ink-muted">{pack.summary}</p> : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {pack?.topics.map((topic) => {
          const selected = state.topicIds.includes(topic.topicId);
          return (
            <div
              key={topic.topicId}
              className={cn(
                "rounded-xl border bg-white p-4",
                selected ? "border-brand-500 ring-1 ring-brand-500" : "border-slate-300",
              )}
            >
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={selected}
                  onChange={() => toggleTopic(topic.topicId)}
                />
                <span>
                  <span className="block font-semibold text-ink">{topic.title}</span>
                  <span className="block text-xs text-ink-muted">{topic.terms.length} search terms</span>
                </span>
              </label>
              <details className="mt-2">
                <summary className="cursor-pointer text-sm font-medium text-brand-700">See terms</summary>
                <ul className="mt-2 list-disc pl-5 text-sm text-ink-soft">
                  {topic.terms.map((t) => (
                    <li key={t.term}>
                      {t.term}
                      {t.proximity ? ` (proximity ${t.proximity})` : ""}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-xs text-ink-muted">
                  {topicLanguageLabel(topic.language)} ·{" "}
                  {topic.searchScope === "title" ? "title only" : "full publication"}
                </p>
              </details>
            </div>
          );
        })}
      </div>

      <label className="flex items-start gap-3 rounded-xl border border-slate-300 bg-white p-4">
        <input
          type="checkbox"
          className="mt-1"
          checked={state.includeAll}
          onChange={(e) =>
            onChange({ includeAll: e.target.checked, topicIds: e.target.checked ? [] : state.topicIds })
          }
        />
        <span>
          <span className="block font-semibold">Include all publications</span>
          <span className="text-sm text-ink-muted">
            Raw source feed with no topic filter. Use when you want everything from the sources you name.
          </span>
        </span>
      </label>

      <div className="rounded-xl border border-dashed border-slate-300 p-4">
        <button
          type="button"
          className="text-sm font-semibold text-brand-700"
          onClick={() => toggleCustom(!customOpen)}
        >
          {customOpen ? "Remove my own topic" : "Add my own topic"}
        </button>
        {state.customTopic ? (
          <div className="mt-3 grid gap-3">
            <p className="text-sm text-ink-muted">
              Same shape as a pack topic: a title, a language, and keyword rows (optional proximity). Structured
              terms only. Free-form search syntax is platform only.
            </p>
            <input
              className="r1-field"
              placeholder="Topic title"
              value={state.customTopic.title}
              onChange={(e) => patchCustom({ title: e.target.value })}
            />
            <label className="text-sm font-medium text-ink-soft">
              Language
              <select
                className="r1-field mt-1"
                value={state.customTopic.language || defaultTopicLanguage}
                onChange={(e) => patchCustom({ language: e.target.value })}
              >
                {topicLanguages.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.label}
                  </option>
                ))}
              </select>
            </label>
            <p className="text-xs text-ink-muted">
              Keywords should be in {topicLanguageLabel(state.customTopic.language)} for the best match accuracy.
              Search also considers variants of the same word (for example monitor and monitoring).
            </p>
            <div className="grid gap-2">
              <p className="text-sm font-medium text-ink-soft">Keywords</p>
              {state.customTopic.terms.map((row, index) => (
                <div key={index} className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <input
                    className="r1-field min-w-0 flex-1"
                    placeholder="Search term"
                    value={row.term}
                    onChange={(e) =>
                      updateKeyword(
                        index,
                        row.proximity != null
                          ? { term: e.target.value, proximity: row.proximity }
                          : { term: e.target.value },
                      )
                    }
                  />
                  <input
                    className="r1-field w-full sm:w-28"
                    type="number"
                    min={1}
                    max={10000}
                    placeholder="Proximity"
                    value={row.proximity ?? ""}
                    onChange={(e) => {
                      const raw = e.target.value.trim();
                      if (!raw) {
                        updateKeyword(index, { term: row.term });
                        return;
                      }
                      const n = Number(raw);
                      updateKeyword(
                        index,
                        Number.isFinite(n) ? { term: row.term, proximity: n } : { term: row.term },
                      );
                    }}
                  />
                  <button
                    type="button"
                    className="shrink-0 text-sm font-medium text-ink-muted hover:text-brand-700"
                    onClick={() => removeKeyword(index)}
                    aria-label={`Remove keyword ${index + 1}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="justify-self-start text-sm font-semibold text-brand-700"
                onClick={addKeyword}
              >
                Add keyword
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
